import type { ApiState } from "@infobis/as-react-auth/lib/types";
import { ActionReducerMapBuilder, AsyncThunk, CaseReducer, Draft, PayloadAction } from "@reduxjs/toolkit";
import { get } from "radash";
import { NoInfer } from "react-redux";

type Cons<H, T> = T extends readonly any[] ?
    ((h: H, ...t: T) => void) extends ((...r: infer R) => void) ? R : never
    : never;

type Prev = [never, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10,
    11, 12, 13, 14, 15, 16, 17, 18, 19, 20, ...0[]]

type Paths<T, D extends number = 10> = [D] extends [never] ? never : T extends object ?
    { [K in keyof T]-?: [K] | (Paths<T[K], Prev[D]> extends infer P ?
        P extends [] ? never : Cons<K, P> : never
        ) }[keyof T]
    : [];

// type GetWithPaths<BaseType, Path extends Paths<BaseType>> =
//     Path extends []
//         ? BaseType
//         : Path extends readonly [ infer Head extends keyof BaseType, ...infer Tail ]
//             ? GetWithPaths<BaseType[Head], Extract<Tail, Paths<BaseType[Head]>>>
//             : never;

type FulfillMeta<P> = {
    arg: P;
    requestId: string;
    requestStatus: "fulfilled";
}
type RejectedMeta<P> = {
    arg: P;
    requestId: string;
    requestStatus: 'rejected';
    aborted: boolean;
    condition: boolean;
}

export interface ApplyExtraReducerConfig<S, R, T extends string, M, STP> {
    // TODO!
    // prop?: keyof S,
    sliceTarget?: STP
    defaultApiProcessing?: Partial<Record<'pendingReducer' | 'rejectReducer' | 'fulfillReducer', boolean>>
    pendingReducer?: (state: Draft<S>) => NoInfer<S> | void | Draft<NoInfer<S>>
    rejectReducer?: CaseReducer<S, PayloadAction<unknown, T, RejectedMeta<M>>>
    fulfillReducer?: CaseReducer<S, PayloadAction<R, T, FulfillMeta<M>>>
}

export const applyExtraReducer = function <S extends ApiState, R, P>(
    builder: ActionReducerMapBuilder<S>,
    thunk: AsyncThunk<R, P, {rejectValue: {message: string}}>,
    config: ApplyExtraReducerConfig<S, R, string, P, undefined> = {}
) {
    builder.addCase(thunk.rejected, (state, action) => {
        if (config.defaultApiProcessing?.rejectReducer ?? true) {
            state.isApiLoading = false;
            state.apiError = action.payload?.message;
        }
        if (config.rejectReducer) config.rejectReducer(state, action);
    })
    builder.addCase(thunk.pending, (state) => {
        if (config.defaultApiProcessing?.pendingReducer ?? true) {
            state.isApiLoading = true;
            state.apiError = undefined;
        }
        if (config.pendingReducer) {
            return config.pendingReducer(state);
        }
    })
    builder.addCase(thunk.fulfilled, (state, action) => {
        if (config.defaultApiProcessing?.fulfillReducer ?? true) {
            state.isApiLoading = false;
            state.apiError = undefined;
        }
        if (config.fulfillReducer) {
            return config.fulfillReducer(state, action);
        } else {
            //@ts-ignore
            state.data = action.payload
        }
    })
}

export const applyExtraReducerWithPaths = function <S, R, P, STP extends Paths<S>, STV extends ApiState>(
    builder: ActionReducerMapBuilder<S>,
    thunk: AsyncThunk<R, P, {rejectValue: {message: string}}>,
    config: ApplyExtraReducerConfig<S, R, string, P, STP> = {}
) {
    const path = config.sliceTarget?.join('.') || '';
    const getStateByPath = (state: Draft<S>, path: string) => get(state, path) as STV;

    builder.addCase(thunk.rejected, (state, action) => {
        if (config.defaultApiProcessing?.rejectReducer ?? true) {
            getStateByPath(state, path).isApiLoading = false;
            getStateByPath(state, path).apiError = action.payload?.message;
        }
        if (config.rejectReducer) config.rejectReducer(state, action);
    })
    builder.addCase(thunk.pending, (state) => {
        if (config.defaultApiProcessing?.pendingReducer ?? true) {
            getStateByPath(state, path).isApiLoading = true;
            getStateByPath(state, path).apiError = undefined;
        }
        if (config.pendingReducer) {
            return config.pendingReducer(state);
        }
    })
    builder.addCase(thunk.fulfilled, (state, action) => {
        if (config.defaultApiProcessing?.fulfillReducer ?? true) {
            getStateByPath(state, path).isApiLoading = false;
            getStateByPath(state, path).apiError = undefined;
        }
        if (config.fulfillReducer) {
            return config.fulfillReducer(state, action);
        } else {
            //@ts-ignore
            getStateByPath(state, path).data = action.payload
        }
    })
}
